import { SiteViewRaycastConfig } from 'features/siteView/state/siteViewRaycastConfig'
import { Ellipsoid, WGS84_ELLIPSOID } from 'gecko-3d-tiles-renderer'
import React, { Dispatch, FC, ReactNode, SetStateAction, useState } from 'react'
import { Matrix4, Vector2 } from 'three'
import * as THREE from 'three'
import {
  GeckoAssetModel,
  GeckoComponent,
  GeckoComponentDataFrame,
  GeckoSite,
  SiteFeature,
} from 'types'
import { useMemoizedObject } from 'utils/hookHelpers'

type SiteViewContextType = {
  // Site & selected asset data
  site: GeckoSite | undefined
  setSite: Dispatch<SetStateAction<GeckoSite | undefined>>

  selectedAssetModel: GeckoAssetModel | undefined
  setSelectedAssetModel: Dispatch<SetStateAction<GeckoAssetModel | undefined>>

  selectedComponent: GeckoComponent | undefined
  setSelectedComponent: Dispatch<SetStateAction<GeckoComponent | undefined>>

  selectedCDF: GeckoComponentDataFrame | undefined
  setSelectedCDF: Dispatch<SetStateAction<GeckoComponentDataFrame | undefined>>

  // Display various popup menus
  showEditSiteConfigPopupMenu: Vector2 | undefined
  setShowEditSiteConfigPopupMenu: Dispatch<SetStateAction<Vector2 | undefined>>

  // Raycast state
  siteViewRaycastConfig: SiteViewRaycastConfig | undefined
  setSiteViewRaycastConfig: Dispatch<
    SetStateAction<SiteViewRaycastConfig | undefined>
  >
  raycastEnabled: boolean
  setRaycastEnabled: Dispatch<SetStateAction<boolean>>

  // Objects associated with the Tile Renderer
  tilesEllipsoid: Ellipsoid
  setTilesEllipsiod: Dispatch<SetStateAction<Ellipsoid>>

  tilesMatrix: Matrix4
  setTilesMatrix: Dispatch<SetStateAction<Matrix4>>

  // camera associated with scene
  camera: React.RefObject<THREE.PerspectiveCamera> | undefined
  setCamera: Dispatch<
    SetStateAction<React.RefObject<THREE.PerspectiveCamera> | undefined>
  >
}

const SiteViewContext = React.createContext<SiteViewContextType | undefined>(
  undefined
)

const SiteViewContextProvider: React.FC<
  SiteViewContextType & {
    children: React.ReactNode
  }
> = ({ children, ...props }) => {
  const context = useMemoizedObject(props)

  return (
    <SiteViewContext.Provider value={context}>
      {children}
    </SiteViewContext.Provider>
  )
}

export const useSiteViewContext = (): SiteViewContextType => {
  const context = React.useContext(SiteViewContext)
  if (!context)
    throw new Error(
      'useSiteViewContext() must be used within a site view context'
    )
  return context
}

export const DefaultSiteViewContextProvider: FC<{
  currentSite: GeckoSite | undefined
  children: ReactNode
}> = ({ currentSite, children }) => {
  const [site, setSite] = useState<GeckoSite | undefined>(currentSite)

  const [selectedAssetModel, setSelectedAssetModel] = useState<
    GeckoAssetModel | undefined
  >(undefined)

  const [selectedComponent, setSelectedComponent] = useState<
    GeckoComponent | undefined
  >(undefined)

  const [selectedCDF, setSelectedCDF] = useState<
    GeckoComponentDataFrame | undefined
  >(undefined)

  const [showEditSiteConfigPopupMenu, setShowEditSiteConfigPopupMenu] =
    useState<Vector2 | undefined>(undefined)

  const [siteViewRaycastConfig, setSiteViewRaycastConfig] = useState<
    SiteViewRaycastConfig | undefined
  >(undefined)
  const [raycastEnabled, setRaycastEnabled] = useState<boolean>(false)

  const [selectedFeature, setSelectedFeature] = useState<
    SiteFeature | undefined
  >(undefined)

  const [tilesEllipsoid, setTilesEllipsiod] =
    useState<Ellipsoid>(WGS84_ELLIPSOID)

  const [tilesMatrix, setTilesMatrix] = useState<Matrix4>(new Matrix4())
  const [camera, setCamera] = useState<
    React.RefObject<THREE.PerspectiveCamera> | undefined
  >(undefined)
  return (
    <SiteViewContextProvider
      {...{
        site,
        setSite,
        selectedAssetModel,
        setSelectedAssetModel,
        selectedComponent,
        setSelectedComponent,
        selectedCDF,
        setSelectedCDF,
        showEditSiteConfigPopupMenu,
        setShowEditSiteConfigPopupMenu,
        siteViewRaycastConfig,
        setSiteViewRaycastConfig,
        raycastEnabled,
        setRaycastEnabled,
        selectedFeature,
        setSelectedFeature,
        tilesEllipsoid,
        setTilesEllipsiod,
        tilesMatrix,
        setTilesMatrix,
        camera,
        setCamera,
      }}>
      {children}
    </SiteViewContextProvider>
  )
}
